import { useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../api/config";

export default function Login() {
    const navigate = useNavigate();

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [remember, setRemember] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const body = new URLSearchParams();
            body.append("username", username.trim());
            body.append("password", password);

            const res = await fetch(
                `${BASE_URL}/login`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                    body: body.toString(),
                }
            );

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error(data.message || "Invalid username or password");
            }

            const storage = remember ? localStorage : sessionStorage;
            storage.setItem("token", data.data.bearer_token);
            storage.setItem("userId", data.data.user_id);

            // ✅ redirect will now ALWAYS work
            navigate("/select-store", { replace: true });

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-white">
            {/* Left */}
            <div className="w-1/2 hidden md:flex flex-col items-center bg-gray-50 p-8">
                <div className="w-full max-w-md">
                    <img src="/assets/squiggles-logo.png" alt="Squiggles" className="w-56" />
                </div>
                <div className="flex-1 flex items-center justify-center">
                    <img
                        src="/assets/signup-illustration.png"
                        alt="Illustration"
                        className="max-w-full max-h-[80vh]"
                    />
                </div>
            </div>

            {/* Right */}
            <div className="w-full md:w-1/2 flex flex-col justify-center px-8 md:px-20">
                <h1 className="text-4xl font-semibold mb-8">Login</h1>

                <form onSubmit={handleSubmit} className="space-y-6 max-w-xl">
                    <input
                        type="text"
                        placeholder="Username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                        className="w-full border-b pb-3 outline-none"
                    />

                    <div className="relative">
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="w-full border-b pb-3 pr-10 outline-none"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-0 top-1/2 -translate-y-1/2"
                        >
                            {showPassword ? (
                                <EyeSlashIcon className="w-5 h-5 text-gray-500" />
                            ) : (
                                <EyeIcon className="w-5 h-5 text-gray-500" />
                            )}
                        </button>
                    </div>

                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={remember}
                            onChange={(e) => setRemember(e.target.checked)}
                        />
                        Remember me
                    </label>

                    {error && <div className="text-red-600 text-sm">{error}</div>}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-black text-white py-3 rounded-lg disabled:opacity-60"
                    >
                        {loading ? "Logging in..." : "Login"}
                    </button>
                </form>
            </div>
        </div>
    );
}
